import { User } from '../types';
import { FieldTypes, WebEntity } from './types';
import { booleanFormatFn } from '../utils/utils';

const validateComponent = (
  value: string,
  validations: Array<{
    description: string;
    validation: (value: string) => boolean;
  }>
) => {
  const errors = validations.reduce((acc, obj) => {
    if (obj.validation(value)) {
      acc.push(obj.description);
    }
    return acc;
  }, [] as string[]);

  return value && errors.length > 0 ? (
    <ul className='generic-form-validation-container'>
      {errors.map((e, idx) => (
        <li className='generic-form-validation' key={`v_${idx}`}>
          {e}
        </li>
      ))}
    </ul>
  ) : (
    <></>
  );
};

const UserEntity: WebEntity<User> = {
  name: 'user',
  endpoint: 'users',
  referenceColumn: 'userUsername',
  customButtonNames: {
    addButtonName: 'Agregar usuario'
  },
  customDataActions: {
    addModalTitle: 'Agregar usuarios',
    editModalTitle: 'Editar usuarios'
  },
  tableColumns: [
    {
      header: 'ID',
      accessorKey: '_id',
      omitExport: true,
      columnType: 'text',
      enableHiding: false,
      visibleInShowHideMenu: false
    },
    {
      header: 'Nombre Usuario',
      accessorKey: 'userUsername',
      columnType: 'text',
      size: 170
    },
    {
      header: 'Nombres',
      accessorKey: 'userAccountFirstname',
      columnType: 'text',
      size: 150
    },
    {
      header: 'Apellidos',
      accessorKey: 'userAccountLastname',
      columnType: 'text',
      size: 150
    },
    {
      header: 'Email',
      accessorKey: 'userAccountEmail',
      columnType: 'text',
      size: 170
    },
    {
      header: 'Empresa',
      accessorKey: 'userAccountCompanyName',
      columnType: 'text',
      size: 150
    },
    {
      header: 'Ocupacion',
      accessorKey: 'userOccupationName',
      columnType: 'text',
      size: 200
    },
    {
      header: 'Tipo',
      accessorKey: 'userTypeName',
      columnType: 'text',
      size: 150
    },
    {
      header: 'Estudios',
      accessorKey: 'cantidadSimulaciones',
      columnType: 'numeric',
      size: 150
    },
    {
      header: '¿Activo?',
      accessorKey: 'userIsActive',
      columnType: 'boolean',
      size: 150,
      Cell: ({ row }) => booleanFormatFn(row.original?.userIsActive)
    },
    {
      header: 'Login',
      accessorKey: 'providerName',
      columnType: 'text',
      size: 150
    }
  ],

  fields: [
    {
      name: 'Nombre Usuario',
      selector: 'userUsername',
      type: FieldTypes.Text,
      required: true,
      textLowerCase: true,
      validate: (value: string) =>
        validateComponent(value, [
          { description: 'No puede contener espacios', validation: (value) => value.includes(' ') },
          {
            description: 'No puede contener caracteres especiales',
            validation: (value) => !/^[a-z\s]+$/.test(value)
          },
          {
            description: 'El usuario no puede tener menos de 5 caracteres',
            validation: (value) => value.length < 5
          },
          {
            description: 'El usuario no puede tener mas de 20 caracteres',
            validation: (value) => value.length > 20
          }
        ])
    },
    {
      name: 'Nombres',
      selector: 'userAccountFirstname',
      type: FieldTypes.Text,
      required: true,
      validate: (value: string) =>
        validateComponent(value, [
          {
            description: 'No puede contener caracteres especiales',
            validation: (value) => !/^(?=.*[a-zA-Z])[a-zA-ZáéíóúÁÉÍÓÚñÑ\s'-]+$/.test(value)
          },
          {
            description: 'Los nombres no pueden tener menos de 2 caracteres',
            validation: (value) => value.length < 2
          },
          {
            description: 'Los nombres no pueden tener mas de 50 caracteres',
            validation: (value) => value.length > 50
          }
        ])
    },
    {
      name: 'Apellidos',
      selector: 'userAccountLastname',
      type: FieldTypes.Text,
      required: true,
      validate: (value: string) =>
        validateComponent(value, [
          {
            description: 'No puede contener caracteres especiales',
            validation: (value) => !/^(?=.*[a-zA-Z])[a-zA-ZáéíóúÁÉÍÓÚñÑ\s'-]+$/.test(value)
          },
          {
            description: 'Los apellidos no pueden tener menos de 2 caracteres',
            validation: (value) => value.length < 2
          },
          {
            description: 'Los apellidos no pueden tener mas de 50 caracteres',
            validation: (value) => value.length > 50
          }
        ])
    },
    {
      name: 'Email',
      selector: 'userAccountEmail',
      type: FieldTypes.Email,
      required: true,
      validate: (value: string) =>
        validateComponent(value, [
          {
            description: 'El formato no es correcto',
            validation: (value) => !/^\w+([.-_+]?\w+)*@\w+([.-]?\w+)*(\.\w{2,10})+$/.test(value)
          }
        ])
    },
    {
      name: 'Contraseña',
      selector: 'userPassword',
      type: FieldTypes.Password,
      required: true,
      validate: (value: string) =>
        validateComponent(value, [
          { description: 'No puede contener espacios', validation: (value) => value.includes(' ') },
          {
            description: 'Debe incluir al menos una minúscula',
            validation: (value) => !/[a-z]/.test(value)
          },
          {
            description: 'Debe incluir al menos una mayúscula',
            validation: (value) => !/[A-Z]/.test(value)
          },
          {
            description: 'Debe incluir al menos un caracter especial',
            validation: (value) => !/[!@#$%^&*(),.?":{}|<>]/.test(value)
          },
          {
            description: 'Debe incluir al menos un número',
            validation: (value) => !/\d/.test(value)
          },
          {
            description: 'Debe tener 8 caracteres como mínimo',
            validation: (value) => value.length < 8
          },
          {
            description: 'No puede tener mas de 20 caracteres',
            validation: (value) => value.length > 20
          }
        ])
    },
    {
      name: 'Empresa',
      selector: 'userAccountCompanyName',
      type: FieldTypes.Text,
      required: true,
      validate: (value: string) =>
        validateComponent(value, [
          {
            description: 'La empresa no puede tener menos de 2 caracteres',
            validation: (value) => value.length < 2
          },
          {
            description: 'La empresa no puede tener mas de 50 caracteres',
            validation: (value) => value.length > 50
          }
        ])
    },
    {
      name: 'Cargo en la Empresa',
      selector: 'userOccupationId',
      type: FieldTypes.Reference,
      required: true,
      reference: {
        name: 'userOccupation',
        endpoint: 'userOccupation',
        select: '_id',
        show: 'userOccupationName'
      }
    },
    {
      name: 'Tipo',
      selector: 'userAccountTypeId',
      type: FieldTypes.Reference,
      required: true,
      reference: {
        name: 'userType',
        endpoint: 'userAccountTypes',
        select: '_id',
        show: 'userTypeName'
      }
    },
    {
      name: '¿Activo?',
      selector: 'userIsActive',
      type: FieldTypes.Boolean,
      required: true
    }
  ],

  editableFields: [
    {
      name: 'Nombre Usuario',
      selector: 'userUsername',
      type: FieldTypes.Text,
      required: true,
      textLowerCase: true,
      validate: (value: string) =>
        validateComponent(value, [
          { description: 'No puede contener espacios', validation: (value) => value.includes(' ') },
          {
            description: 'No puede contener caracteres especiales',
            validation: (value) => !/^[a-z\s]+$/.test(value)
          },
          {
            description: 'El usuario no puede tener menos de 5 caracteres',
            validation: (value) => value.length < 5
          },
          {
            description: 'El usuario no puede tener mas de 20 caracteres',
            validation: (value) => value.length > 20
          }
        ])
    },
    {
      name: 'Nombres',
      selector: 'userAccountFirstname',
      type: FieldTypes.Text,
      required: true,
      validate: (value: string) =>
        validateComponent(value, [
          {
            description: 'No puede contener caracteres especiales',
            validation: (value) => !/^(?=.*[a-zA-Z])[a-zA-ZáéíóúÁÉÍÓÚñÑ\s'-]+$/.test(value)
          },
          {
            description: 'Los nombres no pueden tener menos de 2 caracteres',
            validation: (value) => value.length < 2
          },
          {
            description: 'Los nombres no pueden tener mas de 50 caracteres',
            validation: (value) => value.length > 50
          }
        ])
    },
    {
      name: 'Apellidos',
      selector: 'userAccountLastname',
      type: FieldTypes.Text,
      required: true,
      validate: (value: string) =>
        validateComponent(value, [
          {
            description: 'No puede contener caracteres especiales',
            validation: (value) => !/^(?=.*[a-zA-Z])[a-zA-ZáéíóúÁÉÍÓÚñÑ\s'-]+$/.test(value)
          },
          {
            description: 'Los apellidos no pueden tener menos de 2 caracteres',
            validation: (value) => value.length < 2
          },
          {
            description: 'Los apellidos no pueden tener mas de 50 caracteres',
            validation: (value) => value.length > 50
          }
        ])
    },
    {
      name: 'Email',
      selector: 'userAccountEmail',
      type: FieldTypes.Email,
      required: true,
      validate: (value: string) =>
        validateComponent(value, [
          {
            description: 'El formato no es correcto',
            validation: (value) => !/^\w+([.-_+]?\w+)*@\w+([.-]?\w+)*(\.\w{2,10})+$/.test(value)
          }
        ])
    },
    {
      name: 'Contraseña',
      selector: 'userPassword',
      type: FieldTypes.Password,
      required: false,
      validate: (value: string) =>
        validateComponent(value, [
          { description: 'No puede contener espacios', validation: (value) => value.includes(' ') },
          {
            description: 'Debe incluir al menos una minúscula',
            validation: (value) => !/[a-z]/.test(value)
          },
          {
            description: 'Debe incluir al menos una mayúscula',
            validation: (value) => !/[A-Z]/.test(value)
          },
          {
            description: 'Debe incluir al menos un caracter especial',
            validation: (value) => !/[!@#$%^&*(),.?":{}|<>]/.test(value)
          },
          {
            description: 'Debe incluir al menos un número',
            validation: (value) => !/\d/.test(value)
          },
          {
            description: 'Debe tener 8 caracteres como mínimo',
            validation: (value) => value.length < 8
          },
          {
            description: 'No puede tener mas de 20 caracteres',
            validation: (value) => value.length > 20
          }
        ])
    },
    {
      name: 'Empresa',
      selector: 'userAccountCompanyName',
      type: FieldTypes.Text,
      required: true,
      validate: (value: string) =>
        validateComponent(value, [
          {
            description: 'La empresa no puede tener menos de 2 caracteres',
            validation: (value) => value.length < 2
          },
          {
            description: 'La empresa no puede tener mas de 50 caracteres',
            validation: (value) => value.length > 50
          }
        ])
    },
    {
      name: 'Cargo en la Empresa',
      selector: 'userOccupationId',
      type: FieldTypes.Reference,
      required: true,
      reference: {
        name: 'userOccupation',
        endpoint: 'userOccupation',
        select: '_id',
        show: 'userOccupationName'
      }
    },
    {
      name: 'Tipo',
      selector: 'userAccountTypeId',
      type: FieldTypes.Reference,
      required: true,
      reference: {
        name: 'userType',
        endpoint: 'userAccountTypes',
        select: '_id',
        show: 'userTypeName'
      }
    },
    {
      name: '¿Activo?',
      selector: 'userIsActive',
      type: FieldTypes.Boolean,
      required: true
    }
  ]
};

export default UserEntity;
